import { useNotificationActions } from "@/features/Notification";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { dispatchDepartmentForecastUpdated } from "@/features/Sale/HeaderDashboard/lib/departmentForecastEvents";
import { handleApiResponse } from "@/shared/lib/api";
import { useErrorAlert } from "@/shared/lib/useErrorAlert";
import { DepartmentInterface } from "@/shared/model/api.types";
import { MONTH } from "@/shared/model/constants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import departmentForecastApi from "../../../api/departmentForecast.api";
import { useAuth } from '@/features/Auth/lib/useAuth';
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';
import {
  DepartmentForecastComparison,
  DepartmentForecastFormMonth,
  DepartmentForecastMetrics,
  DepartmentForecastPeriodSummary,
  DepartmentForecastSummary,
} from "../../../model/departmentForecast.types";

const EMPTY_METRICS: DepartmentForecastMetrics = {
  turnover: 0,
  grossProfit: 0,
};

const EMPTY_COMPARISON: DepartmentForecastComparison = {
  turnoverDelta: 0,
  grossProfitDelta: 0,
};

const createEmptyPeriodSummary = (): DepartmentForecastPeriodSummary => ({
  forecast: { ...EMPTY_METRICS },
  companyPlan: { ...EMPTY_METRICS },
  comparison: { ...EMPTY_COMPARISON },
});

const createEmptyFormMonths = (): DepartmentForecastFormMonth[] =>
  MONTH.map((_, index) => ({
    month: index + 1,
    turnoverValue: 0,
    grossProfitValue: 0,
  }));

const getDepartmentName = (departmentId: number, departments: DepartmentInterface[]) =>
  departments.find((department) => department.id === departmentId)?.name ?? `Отдел ${departmentId}`;

const normalizeSummary = (
  summary: DepartmentForecastSummary | null,
  departmentId: number,
  departmentName: string,
  year: number
): DepartmentForecastSummary => {
  const monthsMap = new Map(summary?.months?.map((item) => [item.month, item]) ?? []);

  return {
    departmentId: summary?.departmentId ?? departmentId,
    departmentName: summary?.departmentName ?? departmentName,
    year: summary?.year ?? year,
    months: MONTH.map((_, index) => {
      const month = index + 1;
      const item = monthsMap.get(month);

      return {
        month,
        forecast: { ...EMPTY_METRICS, ...item?.forecast },
        companyPlan: { ...EMPTY_METRICS, ...item?.companyPlan },
        comparison: { ...EMPTY_COMPARISON, ...item?.comparison },
      };
    }),
    quarters: {
      Q1: { ...createEmptyPeriodSummary(), ...summary?.quarters?.Q1 },
      Q2: { ...createEmptyPeriodSummary(), ...summary?.quarters?.Q2 },
      Q3: { ...createEmptyPeriodSummary(), ...summary?.quarters?.Q3 },
      Q4: { ...createEmptyPeriodSummary(), ...summary?.quarters?.Q4 },
    },
    yearTotal: { ...createEmptyPeriodSummary(), ...summary?.yearTotal },
  };
};

function useCreateDepartmentForecastPopup(open: boolean) {
  const currentYear = new Date().getFullYear();
  const { subGenDepartments, departments, loading: workspaceLoading, error: workspaceError } = useWorkspace();
  const { user } = useAuth();
  const { showErrorAlert } = useErrorAlert();
  const { topAlert } = useNotificationActions();
  const showErrorAlertRef = useRef(showErrorAlert);
  const topAlertRef = useRef(topAlert);

  showErrorAlertRef.current = showErrorAlert;
  topAlertRef.current = topAlert;

  const availableDepartments = useMemo(() => {
    const source = isSalesSystemAccount(user)
      ? salesAdministrationDepartments(departments)
      : (subGenDepartments.length > 0 ? subGenDepartments : departments);
    return [...source].sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [departments, subGenDepartments, user]);

  const [departmentId, setDepartmentId] = useState(0);
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState<DepartmentForecastSummary | null>(null);
  const [formMonths, setFormMonths] = useState<DepartmentForecastFormMonth[]>(createEmptyFormMonths());
  const [dirtyMonths, setDirtyMonths] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [savingYear, setSavingYear] = useState(false);
  const [savingMonth, setSavingMonth] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const applySummary = useCallback((
    nextSummary: DepartmentForecastSummary | null,
    nextDepartmentId = departmentId,
    nextYear = year
  ) => {
    if (!nextDepartmentId) {
      return;
    }

    const departmentName = getDepartmentName(nextDepartmentId, availableDepartments);
    const normalized = normalizeSummary(nextSummary, nextDepartmentId, departmentName, nextYear);

    setSummary(normalized);
    setFormMonths(
      normalized.months.map((item) => ({
        month: item.month,
        turnoverValue: item.forecast.turnover,
        grossProfitValue: item.forecast.grossProfit,
      }))
    );
    setDirtyMonths({});
  }, [availableDepartments, departmentId, year]);

  useEffect(() => {
    if (availableDepartments.length === 0) {
      setDepartmentId(0);
      return;
    }

    if (!availableDepartments.some((department) => department.id === departmentId)) {
      setDepartmentId(availableDepartments[0].id);
    }
  }, [availableDepartments, departmentId]);

  useEffect(() => {
    if (!open || !departmentId || !year) {
      return;
    }

    const fetchSummary = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const response = await departmentForecastApi.getDepartmentForecast(departmentId, year);
        const result = handleApiResponse<DepartmentForecastSummary>(response, {
          showErrorAlert: showErrorAlertRef.current,
          topAlert: topAlertRef.current,
        });

        if (result) {
          applySummary(result, departmentId, year);
        } else {
          applySummary(null, departmentId, year);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error_message || error?.message || "Ошибка загрузки прогноза отдела";
        setLoadError(errorMessage);
        showErrorAlertRef.current(errorMessage);
        applySummary(null, departmentId, year);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [open, departmentId, year, applySummary]);

  const currentSummary = useMemo(() => {
    if (!departmentId) {
      return null;
    }

    return normalizeSummary(summary, departmentId, getDepartmentName(departmentId, availableDepartments), year);
  }, [summary, departmentId, availableDepartments, year]);

  const monthRows = useMemo(() => {
    return formMonths.map((month) => {
      const savedMonth = currentSummary?.months.find((item) => item.month === month.month);
      const companyPlan = savedMonth?.companyPlan ?? EMPTY_METRICS;

      return {
        month: month.month,
        turnoverValue: month.turnoverValue,
        grossProfitValue: month.grossProfitValue,
        companyPlan,
        comparison: {
          turnoverDelta: month.turnoverValue - companyPlan.turnover,
          grossProfitDelta: month.grossProfitValue - companyPlan.grossProfit,
        },
        isDirty: Boolean(dirtyMonths[month.month]),
      };
    });
  }, [currentSummary, dirtyMonths, formMonths]);

  const handleYearChange = (value: string) => {
    const parsed = Number(value);
    setYear(Number.isFinite(parsed) && parsed > 0 ? parsed : currentYear);
  };

  const handleMonthFieldChange = (
    month: number,
    field: "turnoverValue" | "grossProfitValue",
    value: string
  ) => {
    const numericValue = Math.max(Number(value) || 0, 0);

    setFormMonths((prev) => {
      const next = prev.map((item) =>
        item.month === month
          ? {
              ...item,
              [field]: numericValue,
            }
          : item
      );

      const nextMonth = next.find((item) => item.month === month);
      const savedMonth = currentSummary?.months.find((item) => item.month === month);
      const isDirty =
        (savedMonth?.forecast.turnover ?? 0) !== (nextMonth?.turnoverValue ?? 0) ||
        (savedMonth?.forecast.grossProfit ?? 0) !== (nextMonth?.grossProfitValue ?? 0);

      setDirtyMonths((prevDirty) => ({
        ...prevDirty,
        [month]: isDirty,
      }));

      return next;
    });
  };

  const saveMonth = async (month: number) => {
    const monthData = formMonths.find((item) => item.month === month);

    if (!departmentId || !year || !monthData) {
      topAlertRef.current("Заполните отдел, год и значения месяца", "warning", 1000);
      return;
    }

    setSavingMonth(month);
    setLoadError(null);

    try {
      const response = await departmentForecastApi.saveDepartmentForecastMonth({
        departmentId,
        year,
        month,
        turnoverValue: monthData.turnoverValue,
        grossProfitValue: monthData.grossProfitValue,
      });

      const result = handleApiResponse<DepartmentForecastSummary>(response, {
        showErrorAlert: showErrorAlertRef.current,
        topAlert: topAlertRef.current,
      });

      if (result) {
        applySummary(result, departmentId, year);
        dispatchDepartmentForecastUpdated();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error_message || error?.message || "Ошибка сохранения месяца";
      setLoadError(errorMessage);
      showErrorAlertRef.current(errorMessage);
    } finally {
      setSavingMonth(null);
    }
  };

  const saveYear = async () => {
    if (!departmentId || !year) {
      topAlertRef.current("Выберите отдел и год", "warning", 1000);
      return;
    }

    setSavingYear(true);
    setLoadError(null);

    try {
      const response = await departmentForecastApi.saveDepartmentForecastYear({
        departmentId,
        year,
        months: formMonths.map((item) => ({
          month: item.month,
          turnoverValue: item.turnoverValue,
          grossProfitValue: item.grossProfitValue,
        })),
      });

      const result = handleApiResponse<DepartmentForecastSummary>(response, {
        showErrorAlert: showErrorAlertRef.current,
        topAlert: topAlertRef.current,
      });

      if (result) {
        applySummary(result, departmentId, year);
        dispatchDepartmentForecastUpdated();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error_message || error?.message || "Ошибка сохранения прогноза";
      setLoadError(errorMessage);
      showErrorAlertRef.current(errorMessage);
    } finally {
      setSavingYear(false);
    }
  };

  return {
    availableDepartments,
    currentSummary,
    departmentId,
    year,
    monthRows,
    loading,
    loadError,
    savingYear,
    savingMonth,
    departmentsLoading: workspaceLoading.departments,
    departmentsError: workspaceError.departments,
    hasDepartments: availableDepartments.length > 0,
    setDepartmentId,
    handleYearChange,
    handleMonthFieldChange,
    saveMonth,
    saveYear,
  };
}

export default useCreateDepartmentForecastPopup;
